import React, { useState, useRef, useEffect } from 'react';
import { Tooltip } from 'react-tooltip';

import styles from '../DocumentSettings/DocumentSettings.module.css';

/**
 * DocumentSettingsSection Component
 * 
 * Reusable component for managing document settings including:
 * - Brand selection
 * - Tags management
 * - Advanced settings (print & sign, re-assign)
 * - Download options
 * - Expiration time configuration
 * - Alert before expiry
 * - Auto reminders
 * 
 * Props:
 *   - brandsList: Array of available brands
 *   - brand: Currently selected brand object
 *   - onBrandSelect: Callback when brand is selected
 *   - tags: Array of current tags
 *   - onTagsChange: Callback when tags change
 *   - enablePrintAndSign: Boolean state
 *   - onAllowSignersPrintAndSignChange: Callback for toggle
 *   - enableReassign: Boolean state
 *   - onAllowSignersReassignChange: Callback for toggle
 *   - documentDownloadOption: Current download option ID
 *   - onDownloadOptionSelect: Callback for download option selection
 *   - expiryValue: String for expiration Days
 *   - onDaysChange: Callback for Days change
 *   - onDaysBlur: Callback for Days blur
 *   - calculatedDateTime: Calculated expiration datetime
 *   - enableExpiryAlert: Boolean state
 *   - onAlertBeforeExpiryChange: Callback for alert toggle
 *   - enableAutoReminder: Boolean state
 *   - onAutoReminderChange: Callback for auto reminder toggle
 *   - reminderDays: String for reminder days
 *   - onRemindDaysChange: Callback for reminder days change
 *   - onRemindDaysBlur: Callback for reminder days blur
 *   - reminderCount: String for max reminders
 *   - onMaxRemindersChange: Callback for max reminders change
 *   - onMaxRemindersBlur: Callback for max reminders blur
 *   - reminderInfo: Object with reminder validation info
 *   - errors: Errors object for validation
 *   - dropdownRefs: Ref object for dropdowns
 *   - activeDropdown: Current active dropdown state
 *   - onToggleDropdown: Callback to toggle dropdown
 *   - highlightedIndices: Object with highlighted indices for dropdowns
 *   - onHighlightChange: Callback for highlight changes
 */

/**
 * DocumentSettingsSection - Self-contained component for managing document settings
 * 
 * This component manages all document settings state and logic internally, following
 * the InternationalPhoneInput.jsx pattern for encapsulation and reusability.
 * 
 * Props:
 *   - brandsList: Array of available brands from API (required)
 *   - onSettingsChange: Callback when any setting changes (receives complete settings object)
 * 
 * Internal State Management:
 *   - 22 state variables for all settings
 *   - 25+ handler functions for user interactions
 *   - 8+ useEffects for initialization, validation, and side effects
 *   - Complete dropdown, tag, days, and reminder logic
 */
const DocumentSettingsSection = ({ brandsList = [], onSettingsChange = () => {}, initialSettings= {}, mode="document" }) => {

  // STATE VARIABLES - All document settings
  
  // Brand selection
  const [brand, setBrand] = useState(null);
  const [highlightedBrandIndex, setHighlightedBrandIndex] = useState(-1);
  
  // Tags
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  
  // Advanced settings toggles
  const [enablePrintAndSign, setenablePrintAndSign] = useState(false);
  const [enableReassign, setenableReassign] = useState(true);

  // Download options
  const [documentDownloadOption, setdocumentDownloadOption] = useState('combined');
  const [highlightedDownloadIndex, setHighlightedDownloadIndex] = useState(-1);
  const [documentDownloadOptions] = useState([
    { id: 'combined', name: 'Combined' },
    { id: 'individually', name: 'Individually' }
  ]);
  
  // Expiration time
  const [expiryValue, setExpiryDays] = useState('60');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [calculatedDateTime, setCalculatedDateTime] = useState(new Date());
  
  // Alert and reminders
  const [enableExpiryAlert, setEnableExpiryAlert] = useState(false);
  const [enableAutoReminder, setEnableAutoReminder] = useState(false);
  const [reminderDays, setReminderDays] = useState('2');
  const [reminderCount, setReminderCount] = useState('3');
  
  // UI state
  const [activeDropdown, setActiveDropdown] = useState({ type: null, id: null });
  const [showAdvanceDocumentSetting, setShowAdvanceDocumentSetting] = useState(false);
  const [errors, setErrors] = useState({ reminder: '' });
  
  // Refs
  const dropdownRefs = useRef({});
  const lastSettingsRef = useRef(null);
  const initializedRef = useRef(false);
  const isInitializingRef = useRef(true);

  // For templateCreate
  const isTemplateCreate = mode === 'template';

  // For templateSend
  const isTemplateSend = mode === 'templateSend';

  // UTILITY FUNCTIONS

  const formatDateTime = (dateTime) => {
    const day = String(dateTime.getDate()).padStart(2, '0');
    const month = String(dateTime.getMonth() + 1).padStart(2, '0');
    const year = dateTime.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const calculateDateTimeByDays = (baseDateTime, days) => {
    const newDate = new Date(baseDateTime);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  };

  const calculateReminderInfo = () => {
    const expiryDay = parseInt(expiryValue, 10);
    const reminderDay = parseInt(reminderDays, 10);

    if (!enableAutoReminder) {
      return {
        isValid: true,
        message: '',
        reminderDateTime: null
      };
    }

    if (reminderDay >= expiryDay) {
      return {
        isValid: false,
        message: 'Reminder days or reminder count should be less than expiry days',
        reminderDateTime: null
      };
    }
    const reminderDates = [];

    for (let i = 1; i <= parseInt(reminderCount, 10); i++) {
      const offsetDays = i * reminderDays;
    
      if (offsetDays >= expiryValue) {
        break;
      }
    
      const reminderDate = new Date(currentDateTime);
    
      reminderDate.setDate(
        reminderDate.getDate() + offsetDays
      );
    
      reminderDates.push(formatDateTime(reminderDate)
          .split(' ')
          .slice(0, 1)
          .join(' ')
      );
    }

    return {
      isValid: true,
      message: `Reminders will be sent on ${reminderDates.join(', ')}`,
      reminderDateTime: reminderDates
    };
  };

  const preventInvalidKeys = (e) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // BRAND HANDLERS
  const handleBrandSelect = (brand) => {
    setBrand(brand);
    setActiveDropdown({ type: null, id: null });
    setHighlightedBrandIndex(-1);
  };

  const handleBrandKeyDown = (e) => {
    if (!activeDropdown || activeDropdown.type !== 'brand') return;
    if (brandsList.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedBrandIndex(prev => {
        const next = prev + 1;
        return next >= brandsList.length ? 0 : next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedBrandIndex(prev => {
        const next = prev - 1;
        return next < 0 ? brandsList.length - 1 : next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedBrandIndex >= 0 && highlightedBrandIndex < brandsList.length
        ? highlightedBrandIndex
        : brandsList.findIndex(b => b.id === brand?.id);
      if (idx >= 0) {
        handleBrandSelect(brandsList[idx]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setActiveDropdown({ type: null, id: null });
      setHighlightedBrandIndex(-1);
    }
  };

  // TAGS HANDLERS
  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 50) {
      const newTags = [...tags, trimmedTag];
      setTags(newTags);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    setTags(newTags);
  };

  const handleTagInputBlur = () => {
    if (tagInput.trim()) {
      handleAddTag();
    }
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      e.preventDefault();
      const lastTag = tags[tags.length - 1];
      handleRemoveTag(lastTag);
    }
  };

  const handleTagPaste = (e) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData('text');

    const pastedTags = pastedText
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);

    if (pastedTags.length === 0) {
      return;
    }

    const uniqueTags = [
      ...new Set([
        ...tags,
        ...pastedTags
      ])
    ].slice(0, 50);

    setTags(uniqueTags);
    setTagInput('');
  };

  // DOWNLOAD OPTIONS HANDLERS
  const handleDownloadOptionSelect = (option) => {
    setdocumentDownloadOption(option.id);
    setActiveDropdown({ type: null, id: null });
    setHighlightedDownloadIndex(-1);
    onSettingsChange({ documentDownloadOption: option.id });
  };

  const handleDownloadOptionKeyDown = (e) => {
    if (!activeDropdown || activeDropdown.type !== 'downloadOption') return;
    if (documentDownloadOptions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedDownloadIndex(prev => {
        const next = prev + 1;
        return next >= documentDownloadOptions.length ? 0 : next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedDownloadIndex(prev => {
        const next = prev - 1;
        return next < 0 ? documentDownloadOptions.length - 1 : next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedDownloadIndex >= 0 && highlightedDownloadIndex < documentDownloadOptions.length
        ? highlightedDownloadIndex
        : documentDownloadOptions.findIndex(o => o.id === documentDownloadOption);
      if (idx >= 0) {
        handleDownloadOptionSelect(documentDownloadOptions[idx]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setActiveDropdown({ type: null, id: null });
      setHighlightedDownloadIndex(-1);
    }
  };

  // ADVANCED SETTINGS HANDLERS (Toggles)
  const handleAllowSignersPrintAndSignChange = (e) => {
    const newValue = e.target.checked;
    setenablePrintAndSign(newValue);
  };

  const handleAllowSignersReassignChange = (e) => {
    const newValue = e.target.checked;
    setenableReassign(newValue);
  };

  // EXPIRATION TIME HANDLERS
  const handleDaysChange = (e) => {
    const value = e.target.value;

    if (value === '') {
      setExpiryDays('');
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    let num = parseInt(value, 10);

    if (!isNaN(num)) {
      setExpiryDays(Math.min(num, 180).toString());
    }

    setCalculatedDateTime(calculateDateTimeByDays(new Date(), num));
    onSettingsChange({ expiryValue: String(num) });
  };

  const handleDaysBlur = () => {
    let value = parseInt(expiryValue, 10);

    if (isNaN(value)) {
      value = 60;
    }

    value = Math.min(Math.max(value, 1), 180);

    setExpiryDays(String(value));
    setCalculatedDateTime(calculateDateTimeByDays(new Date(), value));
    onSettingsChange({ expiryValue: String(value) });
  };

  const handleIncreaseDays = () => {
    const current = parseInt(expiryValue || '60', 10);
    const newValue = Math.min(current + 1, 180);

    setExpiryDays(String(newValue));

    const calculated = calculateDateTimeByDays(
      currentDateTime,
      newValue
    );

    setCalculatedDateTime(calculated);
  };

  const handleDecreaseDays = () => {
    const current = parseInt(expiryValue || '60', 10);
    const newValue = Math.max(current - 1, 1);

    setExpiryDays(String(newValue));

    const calculated = calculateDateTimeByDays(
      currentDateTime,
      newValue
    );

    setCalculatedDateTime(calculated);
  };

  // ALERT & REMINDER HANDLERS
  const handleAlertBeforeExpiryChange = (e) => {
    const newValue = e.target.checked;
    setEnableExpiryAlert(newValue);
    onSettingsChange({ enableExpiryAlert: newValue });
  };

  const handleAutoReminderChange = (e) => {  
    if (parseInt(expiryValue, 10) >= 2) {
      setEnableAutoReminder(e.target.checked);
    }

  };

  const handleRemindDaysChange = (e) => {
    const value = e.target.value;

    if (value === '') {
      setReminderDays('');
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    let num = parseInt(value, 10);

    if (num > 180) {
      num = 180;
    }

    setReminderDays(String(num));
    onSettingsChange({ reminderDays: String(num) });
  };

  const handleRemindDaysBlur = () => {
    let value = parseInt(reminderDays, 10);

    if (isNaN(value)) {
      value = 1;
    }

    value = Math.max(1, value);

    setReminderDays(value.toString());
  };

  const handleIncreaseRemindDays = () => {
    const current = parseInt(reminderDays || '2', 10);
    const newValue = Math.min(current + 1, 180);

    setReminderDays(String(newValue));
    onSettingsChange({ reminderDays: String(newValue) });
  };

  const handleDecreaseRemindDays = () => {
    const current = parseInt(reminderDays || '2', 10);
    const newValue = Math.max(current - 1, 1);

    setReminderDays(String(newValue));
    onSettingsChange({ reminderDays: String(newValue) });
  };

  const handleMaxRemindersChange = (e) => {
    const value = e.target.value;

    if (value === '') {
      setReminderCount('');
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    let num = parseInt(value, 10);

    if (num > 5) {
      num = 5;
    }

    setReminderCount(String(num));
    onSettingsChange({ reminderCount: String(num) });
  };

  const handleMaxRemindersBlur = () => {
    let value = parseInt(reminderCount, 10);

    if (isNaN(value)) {
      value = 3;
    }

    value = Math.max(1, Math.min(value, 5));

    setReminderCount(String(value));
    onSettingsChange({ reminderCount: String(value) });
  };

  const handleIncreaseMaxReminders = () => {
    const current = parseInt(reminderCount || '3', 10);
    const newValue = Math.min(current + 1, 5);

    setReminderCount(String(newValue));
    onSettingsChange({ reminderCount: String(newValue) });
  };

  const handleDecreaseMaxReminders = () => {
    const current = parseInt(reminderCount || '3', 10);
    const newValue = Math.max(current - 1, 1);

    setReminderCount(String(newValue));
    onSettingsChange({ reminderCount: String(newValue) });
  };

  // DROPDOWN TOGGLE HANDLER
  const toggleDropdown = (type, id) => {
    if (activeDropdown.type === type && activeDropdown.id === id) {
      setActiveDropdown({ type: null, id: null });
      if (type === 'brand') setHighlightedBrandIndex(-1);
      if (type === 'downloadOption') setHighlightedDownloadIndex(-1);
    } else {
      setActiveDropdown({ type, id });
      if (type === 'brand') {
        const idx = brand ? brandsList.findIndex(b => b.id === brand.id) : -1;
        setHighlightedBrandIndex(idx);
      }
      if (type === 'downloadOption') {
        const idx = documentDownloadOptions.findIndex(o => o.id === documentDownloadOption);
        setHighlightedDownloadIndex(idx);
      }
    }
  };

  // USE EFFECTS
  useEffect(() => {
    if (!initialSettings) return;

    setBrand(initialSettings.brand || null);
    const normalizedTags = (initialSettings.tags || [])
      .reduce((acc, tag) => {
        if (typeof tag === 'string') {
          return [
            ...acc,
            ...tag.split(',').map(t => t.trim()).filter(Boolean)
          ];
        }
        return acc;
      }, []);
    
    setTags(normalizedTags);

    setenablePrintAndSign(
      initialSettings.enablePrintAndSign ?? false
    );
    setenableReassign(
      initialSettings.enableReassign ?? true
    );
  }, [isTemplateSend ? initialSettings : '']);

  useEffect(() => {
    if (initialSettings?.brand) {
      setBrand(initialSettings.brand);
      isInitializingRef.current = true;
    }
  }, [initialSettings?.brand]);

  useEffect(() => {
    if (isInitializingRef.current) {
      isInitializingRef.current = false;
      return;
    }

    const settings = {
      brand,
      tags,
      enablePrintAndSign,
      enableReassign,
      documentDownloadOption,
      expiryValue,
      enableExpiryAlert,
      enableAutoReminder,
      reminderDays,
      reminderCount
    };

    const current = JSON.stringify(settings);

    if (lastSettingsRef.current !== current) {
      lastSettingsRef.current = current;
      onSettingsChange(settings);
    }
  }, [
    brand,
    tags,
    enablePrintAndSign,
    enableReassign,
    documentDownloadOption,
    expiryValue,
    enableExpiryAlert,
    enableAutoReminder,
    reminderDays,
    reminderCount,
    onSettingsChange
  ]);

  // Initialize datetime on component mount
  useEffect(() => {
    const now = new Date();
    setCurrentDateTime(now);
    const calculated = calculateDateTimeByDays(now, 60);
    setCalculatedDateTime(calculated);
  }, []);

  // Auto-select default brand when brands list is loaded
  // BUT: Don't override if initialSettings already has a brand
  useEffect(() => {
    // parent is controlling brand selection
    if (initialSettings?.brand) {
      setBrand(initialSettings.brand);
      return;
    }

    if (brandsList.length > 0 && !brand) {
      const defaultBrand = brandsList.find(b => b.isDefault);
      if (defaultBrand) {
        setBrand(defaultBrand);
      }
    }
  }, [brandsList, initialSettings?.brand]);

  // Auto-reset auto reminder when hours drop below 48
  useEffect(() => {
    if (parseInt(expiryValue, 10) <= 1 && enableAutoReminder) {
      setEnableAutoReminder(false);
      setErrors(prev => ({
        ...prev,
        reminder: ''
      }));
    }
  }, [expiryValue, enableAutoReminder]);

  // Update reminder error when reminder settings change
  useEffect(() => {
    const reminderInfo = calculateReminderInfo();
    if (!reminderInfo.isValid) {
      setErrors(prev => ({ ...prev, reminder: reminderInfo.message }));
    } else {
      setErrors(prev => ({ ...prev, reminder: '' }));
    }
  }, [enableAutoReminder, expiryValue, reminderDays, reminderCount]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeDropdown.type === 'brand') {
        const brandRef = dropdownRefs.current?.['brand-brand'];
        if (brandRef && !brandRef.contains(e.target)) {
          setActiveDropdown({ type: null, id: null });
          setHighlightedBrandIndex(-1);
        }
      }
      if (activeDropdown.type === 'downloadOption') {
        const downloadRef = dropdownRefs.current?.['downloadOption-downloadOption'];
        if (downloadRef && !downloadRef.contains(e.target)) {
          setActiveDropdown({ type: null, id: null });
          setHighlightedDownloadIndex(-1);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // RENDER
  const reminderInfo = calculateReminderInfo();

  return (
    <div className="contentSection">
      <div className="contentBox">
        <div className="contentBoxHeader">
          <span>Document Settings</span>
        </div>
        <div className="document-fields">
          {/* Brand Selection */}
          {brandsList.length !== 0 && <div className="field-group">
            <label htmlFor="brand-select" className={styles.titleLabel}>
              <span>Select Brand</span>
              <span className={`${styles.iconBase} ${styles.titleInfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="brand-tooltip"></span>
            </label>
            <Tooltip
              id="brand-tooltip"
              place="right"
              className='tooltip-info tip-title'
              render={() => (
                <div className="tooltip-content">
                  Select the branding that you would like your recipients to see. It is possible to create and manage multiple brands from Administrator Settings.
                </div>
              )}
            />
            <div className={`custom-dropdown`} ref={el => dropdownRefs.current[`brand-brand`] = el}>
              <button
                type="button"
                className={`dropdown-toggle-language ${styles.brandDropDown}`}
                onClick={() => toggleDropdown('brand', 'brand')}
                onKeyDown={handleBrandKeyDown}
                aria-haspopup="listbox"
                aria-expanded={activeDropdown.type === 'brand' && activeDropdown.id === 'brand'}
                disabled={brandsList.length === 0}
                title={brandsList.length === 0 ? 'No brands available' : 'Select a brand'}
              >
                <span className="language-code">
                  {brand?.brandName || 'Select Brand'}
                  <span className={`${styles.languageDropDownIcon} sfIconBase sfDropDownIcon`}></span>
                </span>
              </button>
              {activeDropdown.type === 'brand' && activeDropdown.id === 'brand' && (
                <div className={`dropdown-menu ${styles.dropDownMenu}`} role="listbox" >
                  {brandsList.map((currentBrand, index) => (
                    <div
                      key={brand.id}
                      role="option"
                      aria-selected={brand?.id === currentBrand.id}
                      tabIndex={0}
                      ref={el => { if (index === highlightedBrandIndex && el) el.scrollIntoView({ block: 'nearest' }); }}
                      className={`dropdown-item ${styles.dropdownItem} ${brand?.id === currentBrand.id ? 'selected' : ''} ${index === highlightedBrandIndex ? 'highlighted' : ''}`}
                      onClick={() => handleBrandSelect(currentBrand)}
                      onMouseEnter={() => setHighlightedBrandIndex(index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleBrandSelect(currentBrand);
                        }
                      }}
                    >
                      {currentBrand.brandName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>}

          {/* Tags Input */}
          <div className="field-group">
            <label htmlFor="tags-input" className={styles.titleLabel}>
              <span>Tags</span>
              <span className={`${styles.iconBase} ${styles.titleInfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="tags-tooltip"></span>
            </label>
            <Tooltip
              id="tags-tooltip"
              place="right"
              className='tooltip-info tip-title'
              render={() => (
                <div className="tooltip-content">
                  Add tags to organize and categorize your documents. Tags are stored locally and not sent to the backend.
                </div>
              )}
            />
            <div className={styles.tagsInput}>
              {tags.map((tag, index) => (
                <div key={index} className={`${styles.tagChip}`}>
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className={`${styles.removeTag}`}
                    title="Remove tag"
                    aria-label={`Remove tag: ${tag}`}
                  >
                    <span className={`sfIconBase sfCloseIcon ${styles.closeIcon}`}></span>
                  </button>
                </div>
              ))}
              <input
                id="tags-input"
                type="text"
                value={tagInput}
                placeholder={tags.length === 0 ? 'Enter one or more tags separated by a comma.' : ''}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyPress}
                onBlur={handleTagInputBlur}
                onPaste={handleTagPaste}
                className={styles.tagInputField}
                maxLength="50"
                aria-label="Add new tag"
              />
            </div>
          </div>
          {/* Allow Signers to Print and Sign */}
          {isTemplateCreate && (<div className="field-group">
            <div className={styles.availableSettings}>
              <label className={`${styles.toggleSwitch} toggle-switch`} aria-label="Settings Toggle">
                <input
                  type="checkbox"
                  checked={enablePrintAndSign}
                  onChange={handleAllowSignersPrintAndSignChange}
                />
                <span className={`${styles.toggleSlider} toggle-slider`}></span>
              </label>
              <span className={styles.signerSettings}>Allow signers to print and sign</span>
              <button className={`${styles.fieldHelpButton} help-button`}>
                <span className={`${styles.iconBase} ${styles.fieldConfigurationinfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="field-print-sign"></span>
              </button>
              <Tooltip
                id="field-print-sign"
                place="top"
                className='tooltip-info tip-title'
                render={() => (
                  <div className="tooltip-content">
                    Enabling this option provides the signer with an option to download, print, and sign the document. A scanned copy in PDF format is then sent to the sender for verification. The sender will verify the document and then re-upload it for other signers to continue signing.
                  </div>
                )}
              />
            </div>
            {/* Allow Signers to Re-assign */}
            <div className={styles.availableSettings}>
              <label className={`${styles.toggleSwitch} toggle-switch`} aria-label="Settings Toggle">
                <input
                  type="checkbox"
                  checked={enableReassign}
                  onChange={handleAllowSignersReassignChange}
                />
                <span className={`${styles.toggleSlider} toggle-slider`}></span>
              </label>
              <span className={styles.signerSettings}>Allow signers to re-assign</span>
              <button className={`${styles.fieldHelpButton} help-button`}>
                <span className={`${styles.iconBase} ${styles.fieldConfigurationinfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="field-re-assign"></span>
              </button>
              <Tooltip
                id="field-re-assign"
                place="top"
                className='tooltip-info tip-title'
                render={() => (
                  <div className="tooltip-content">
                    Enabling this option allows signers to re-assign documents to others.
                  </div>
                )}
              />
            </div>
          </div>)}
          {/* Advanced Settings Toggle */}
          {!isTemplateCreate && (<div className="document-fields">
            <button
              type="button"
              onClick={() => setShowAdvanceDocumentSetting(prev => !prev)}
              className={styles.showAdvanceSetting}
            >
              <span className={styles.advanceSettingTitle}>
                {showAdvanceDocumentSetting ? 'Hide advanced settings' : 'Show advanced settings'}
                <span className={showAdvanceDocumentSetting ? `${styles.sfIconBase} ${styles.sfWrapUp}` : `${styles.sfIconBase} ${styles.sfDropDown}`}></span>
              </span>
            </button>

            {showAdvanceDocumentSetting && (
              <div className='document-fields'>
                {/* Document Download Options */}
                <div className="field-group">
                  <label htmlFor="download-option-select" className={styles.titleLabel}>
                    <span>Document download options</span>
                    <span className={`${styles.iconBase} ${styles.titleInfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="download-option-tooltip"></span>
                  </label>
                  <Tooltip
                    id="download-option-tooltip"
                    place="right"
                    className='tooltip-info tip-title'
                    render={() => (
                      <div className="tooltip-content">
                        Choose whether to download the completed documents merged into a single PDF or as individual documents in a Zip archive
                      </div>
                    )}
                  />
                  <div className={`custom-dropdown`} ref={el => dropdownRefs.current[`downloadOption-downloadOption`] = el}>
                    <button
                      type="button"
                      className={`dropdown-toggle-language ${styles.brandDropDown}`}
                      onClick={() => toggleDropdown('downloadOption', 'downloadOption')}
                      onKeyDown={handleDownloadOptionKeyDown}
                      aria-haspopup="listbox"
                      aria-expanded={activeDropdown.type === 'downloadOption' && activeDropdown.id === 'downloadOption'}
                    >
                      <span className="language-code">
                        {documentDownloadOptions.find(o => o.id === documentDownloadOption)?.name || 'Combined'}
                        <span className={`${styles.languageDropDownIcon} sfIconBase sfDropDownIcon`}></span>
                      </span>
                    </button>
                    {activeDropdown.type === 'downloadOption' && activeDropdown.id === 'downloadOption' && (
                      <div className={`dropdown-menu ${styles.dropDownMenu}`} role="listbox">
                        {documentDownloadOptions.map((option, index) => (
                          <div
                            key={option.id}
                            role="option"
                            aria-selected={documentDownloadOption === option.id}
                            tabIndex={0}
                            ref={el => { if (index === highlightedDownloadIndex && el) el.scrollIntoView({ block: 'nearest' }); }}
                            className={`dropdown-item ${styles.dropdownItem} ${documentDownloadOption === option.id ? 'selected' : ''} ${index === highlightedDownloadIndex ? 'highlighted' : ''}`}
                            onClick={() => handleDownloadOptionSelect(option)}
                            onMouseEnter={() => setHighlightedDownloadIndex(index)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleDownloadOptionSelect(option);
                              }
                            }}
                          >
                            {option.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Allow Signers to Print and Sign */}
                <div className="field-group">
                  <div className={styles.availableSettings}>
                    <label className={`${styles.toggleSwitch} toggle-switch`} aria-label="Settings Toggle">
                      <input
                        type="checkbox"
                        checked={enablePrintAndSign}
                        onChange={handleAllowSignersPrintAndSignChange}
                      />
                      <span className={`${styles.toggleSlider} toggle-slider`}></span>
                    </label>
                    <span className={styles.signerSettings}>Allow signers to print and sign</span>
                    <button className={`${styles.fieldHelpButton} help-button`}>
                      <span className={`${styles.iconBase} ${styles.fieldConfigurationinfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="field-print-sign"></span>
                    </button>
                    <Tooltip
                      id="field-print-sign"
                      place="top"
                      className='tooltip-info tip-title'
                      render={() => (
                        <div className="tooltip-content">
                          Enabling this option provides the signer with an option to download, print, and sign the document. A scanned copy in PDF format is then sent to the sender for verification. The sender will verify the document and then re-upload it for other signers to continue signing.
                        </div>
                      )}
                    />
                  </div>

                  {/* Allow Signers to Re-assign */}
                  <div className={styles.availableSettings}>
                    <label className={`${styles.toggleSwitch} toggle-switch`} aria-label="Settings Toggle">
                      <input
                        type="checkbox"
                        checked={enableReassign}
                        onChange={handleAllowSignersReassignChange}
                      />
                      <span className={`${styles.toggleSlider} toggle-slider`}></span>
                    </label>
                    <span className={styles.signerSettings}>Allow signers to re-assign</span>
                    <button className={`${styles.fieldHelpButton} help-button`}>
                      <span className={`${styles.iconBase} ${styles.fieldConfigurationinfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="field-re-assign"></span>
                    </button>
                    <Tooltip
                      id="field-re-assign"
                      place="top"
                      className='tooltip-info tip-title'
                      render={() => (
                        <div className="tooltip-content">
                          Enabling this option allows signers to re-assign documents to others.
                        </div>
                      )}
                    />
                  </div>
                </div>

                {/* Expiration Time Configuration */}
                <div className="field-group">
                  <span className={styles.commonTitle}>Expires in</span>

                  {/* Hours Container */}
                  <div className={styles.hoursContainer}>
                    <div className={styles.hoursContent}>
                      <input
                        id="expiration-hours-input"
                        type="text"
                        inputMode="numeric"
                        value={expiryValue}
                        onChange={handleDaysChange}
                        onBlur={handleDaysBlur}
                        onKeyDown={preventInvalidKeys}
                        className={styles.hoursInput}
                        style={{
                          width: `${Math.max(expiryValue?.length || 1, 1) + 1}ch`
                        }}
                      />
                      <span className={styles.hoursLabel}>{expiryValue > 1 ? 'Days' : 'Day'}</span>
                    </div>

                    <div className={styles.containerDivider}></div>

                    <div className={styles.arrowContainer}>
                      <button
                        type="button"
                        onClick={handleIncreaseDays}
                        disabled={Number(expiryValue || 60) === 180}
                        className={styles.hoursArrow}
                      >
                        <span className={`${styles.sfIconBase} ${styles.sfWrapUp}`}></span>
                      </button>

                      <button
                        type="button"
                        onClick={handleDecreaseDays}
                        disabled={Number(expiryValue || 1) === 1}
                        className={styles.hoursArrow}
                      >
                        <span className={`${styles.sfIconBase} ${styles.sfDropDown} ${styles.decreaseButton}`}></span>
                      </button>

                    </div>
                  </div>

                  {/* Expiration DateTime Display */}
                  <div className={styles.timeFormat}>
                    {formatDateTime(calculatedDateTime)}
                  </div>

                  {/* Alert Before Expiry */}
                  <div className={styles.availableSettings}>
                    <label>
                      <input
                        type="checkbox"
                        checked={enableExpiryAlert}
                        onChange={handleAlertBeforeExpiryChange}
                        className={styles.expiryAlert}
                      />
                      <span>
                        {parseInt(expiryValue, 10) <= 1 ? 'Alert 2 hours before expiry' : 'Alert 1 day before expiration'}
                      </span>
                    </label>
                  </div>

                  {/* Auto Reminder Toggle */}
                  <div className={`${styles.availableSettings} ${parseInt(expiryValue, 10) <= 1 ? styles.pointerEventNone : ''}`}>
                    <label className={`${styles.toggleSwitch} toggle-switch`} aria-label='toggle-input'>
                      <input
                        type="checkbox"
                        checked={enableAutoReminder}
                        onChange={handleAutoReminderChange}
                        disabled={parseInt(expiryValue, 10) <= 1}
                      />
                      <span className={`${styles.toggleSlider} toggle-slider`}></span>
                    </label>
                    <span className={styles.signerSettings}>Auto reminder</span>
                  </div>

                  {/* Reminder Container */}
                  <div className={`${styles.reminderContainer} ${enableAutoReminder && parseInt(expiryValue, 10) >= 2 ? '' : styles.pointerEventNone}`}>
                    {/* Remind Every Days */}
                    <div>
                      <div className={styles.commonTitle}>
                        Remind every
                      </div>

                      <div className={`${styles.hoursContainer}`}>
                        <div className={styles.hoursContent}>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={reminderDays}
                            onChange={handleRemindDaysChange}
                            onBlur={handleRemindDaysBlur}
                            className={styles.hoursInput}
                            style={{
                              width: `${Math.max(reminderDays?.length || 1, 1) + 1}ch`
                            }}
                          />
                          <span className={styles.hoursLabel}>{reminderDays > 1 ? 'Days' : 'Day'}</span>
                        </div>
                        <div className={styles.containerDivider}></div>
                        <div className={styles.arrowButtons}>
                          <button
                            type="button"
                            className={styles.hoursArrow}
                            onClick={handleIncreaseRemindDays}
                          >
                            <span className={`${styles.sfIconBase} ${styles.sfWrapUp} ${styles.arrow}`}></span>
                          </button>
                          <div className={styles.containerDivider}></div>
                          <button
                            type="button"
                            className={styles.hoursArrow}
                            onClick={handleDecreaseRemindDays}
                          >
                            <span className={`${styles.sfIconBase} ${styles.sfDropDown} ${styles.arrow}`}></span>
                          </button>
                        </div>
                      </div>

                    </div>

                    {/* Max Reminders */}
                    <div>
                      <div className={styles.commonTitle}>
                        Max. reminders (up to 5)
                      </div>

                      <div className={`${styles.hoursContainer} ${styles.reminderTimesContainer}`}>
                        <div className={styles.hoursContent}>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={reminderCount}
                            onChange={handleMaxRemindersChange}
                            onBlur={handleMaxRemindersBlur}
                            className={styles.hoursInput}
                            style={{
                              width: `${Math.max(reminderCount?.length || 1, 1) + 1}ch`
                            }}
                          />
                        </div>
                        <div className={styles.containerDivider}></div>
                        <div className={styles.arrowButtons}>
                          <button
                            type="button"
                            className={styles.hoursArrow}
                            onClick={handleIncreaseMaxReminders}
                          >
                            <span className={`${styles.sfIconBase} ${styles.sfWrapUp} ${styles.arrow}`}></span>
                          </button>
                          <button
                            type="button"
                            className={styles.hoursArrow}
                            onClick={handleDecreaseMaxReminders}
                          >
                            <span className={`${styles.sfIconBase} ${styles.sfDropDown} ${styles.arrow}`}></span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Reminder Info Message */}
                  <div className={styles.timeFormat}>
                    {reminderInfo.message && (
                      <span>{reminderInfo.isValid && reminderInfo.message}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>)}
        </div>
        {/* Reminder Validation Error Display */}
        {errors.reminder && <div className="error-message">{errors.reminder}</div>}
      </div>
    </div>
  );
};

export default DocumentSettingsSection;
